import { ColDef, ColumnApi } from 'ag-grid-community';
import {
  getMobileCriticalFields,
  MOBILE_BREAKPOINT_PX,
  PROTECTED_ALWAYS_VISIBLE_COL_IDS,
} from './config/ag-grid-mobile-config';

export { MOBILE_BREAKPOINT_PX, PROTECTED_ALWAYS_VISIBLE_COL_IDS };

export interface FloraColDef extends ColDef {
  /** When true, column stays visible on mobile. When false, always hidden on mobile. */
  mobileCritical?: boolean;
}

const ACTIONS_COLUMN_MIN_WIDTH = 80;

function getColumnKey(col: ColDef): string | undefined {
  return col.field || col.colId;
}

export function isMobileCriticalColumn(
  col: FloraColDef,
  criticalFields: string[],
): boolean {
  if (col.mobileCritical === true) {
    return true;
  }
  if (col.mobileCritical === false) {
    return false;
  }

  const field = getColumnKey(col);
  if (field && criticalFields.includes(field)) {
    return true;
  }

  if (col.headerName && criticalFields.includes(col.headerName)) {
    return true;
  }

  return false;
}

function applyActionsColumnMobileSizing(col: FloraColDef): FloraColDef {
  const width = typeof col.width === 'number' ? col.width : ACTIONS_COLUMN_MIN_WIDTH;

  return {
    ...col,
    minWidth: ACTIONS_COLUMN_MIN_WIDTH,
    width: Math.max(width, ACTIONS_COLUMN_MIN_WIDTH),
    suppressSizeToFit: true,
  };
}

/** Keep protected columns (e.g. actions) visible when restoring saved column state. */
export function sanitizeColumnState(state: any[] | null | undefined): any[] {
  if (!state?.length) {
    return state ?? [];
  }

  return state.map((col) =>
    isProtectedColumn(col.colId) ? { ...col, hide: false } : col,
  );
}

function isProtectedColumn(colId: string | undefined): boolean {
  return !!colId && (PROTECTED_ALWAYS_VISIBLE_COL_IDS as readonly string[]).includes(colId);
}

/** Apply mobile visibility through column defs (simple grids without saved state). */
export function applyMobileColumnVisibility(
  columnDefs: FloraColDef[],
  isMobile: boolean,
  tableParams?: { mobileCriticalFields?: string[] },
): FloraColDef[] {
  const criticalFields = getMobileCriticalFields(tableParams);

  return columnDefs.map((col) => {
    const field = getColumnKey(col);

    if (!isMobile) {
      return { ...col, hide: col.hide ?? false };
    }

    const isCritical = isMobileCriticalColumn(col, criticalFields);
    let updated: FloraColDef = {
      ...col,
      hide: !isCritical,
    };

    if (field === 'actions') {
      updated = applyActionsColumnMobileSizing(updated);
    }

    return updated;
  });
}

/** Apply mobile visibility via Column API — avoids clobbering column defs on reload. */
export function applyMobileColumnVisibilityViaApi(
  columnApi: ColumnApi,
  columnDefs: FloraColDef[],
  isMobile: boolean,
  tableParams?: { mobileCriticalFields?: string[] },
): void {
  const criticalFields = getMobileCriticalFields(tableParams);

  PROTECTED_ALWAYS_VISIBLE_COL_IDS.forEach((colId) => {
    if (columnApi.getColumn(colId)) {
      columnApi.setColumnVisible(colId, true);
    }
  });

  columnDefs.forEach((col) => {
    const colId = getColumnKey(col);
    if (!colId || isProtectedColumn(colId)) {
      return;
    }

    if (!isMobile) {
      columnApi.setColumnVisible(colId, !(col.hide ?? false));
      return;
    }

    columnApi.setColumnVisible(colId, isMobileCriticalColumn(col, criticalFields));
  });

  if (isMobile && columnApi.getColumn('actions')) {
    const actionsWidth = columnApi.getColumn('actions')?.getActualWidth() ?? ACTIONS_COLUMN_MIN_WIDTH;
    if (actionsWidth < ACTIONS_COLUMN_MIN_WIDTH) {
      columnApi.setColumnWidth('actions', ACTIONS_COLUMN_MIN_WIDTH);
    }
  }
}
